import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import { CouponFormModal } from '../pages/coupons/CouponFormModal';

export interface CouponFormPrefill {
  couponId?: string;
  discountType?: 'percentage' | 'fixed';
  amount?: number;
  maximumAmount?: number;
  minPurchase?: number;
  maxUses?: number;
  expiryDate?: string; // YYYY-MM-DD
  startDate?: string; // YYYY-MM-DD
  description?: string;
  isActive?: boolean;
}

export interface CouponFormHandle {
  setFields: (values: CouponFormPrefill) => void;
}

interface CouponFormContextValue {
  openCreate: () => void;
  close: () => void;
  isOpen: boolean;
  formRef: MutableRefObject<CouponFormHandle | null>;
}

const CouponFormContext = createContext<CouponFormContextValue | null>(null);

export function CouponFormProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const formRef = useRef<CouponFormHandle | null>(null);

  const openCreate = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo<CouponFormContextValue>(
    () => ({ openCreate, close, isOpen, formRef }),
    [openCreate, close, isOpen],
  );

  return (
    <CouponFormContext.Provider value={value}>
      {children}
      <CouponFormModal ref={formRef} isOpen={isOpen} onClose={close} />
    </CouponFormContext.Provider>
  );
}

export function useCouponForm() {
  const ctx = useContext(CouponFormContext);
  if (!ctx) throw new Error('useCouponForm must be used inside CouponFormProvider');
  return ctx;
}
